import * as Sentry from '@sentry/browser'

import { onLogout } from '@/lib/apollo'

import profile from '@/graphql/queries/profile.gql'
import savedCommentReplies from '@/graphql/queries/savedCommentReplies.gql'
import logout from '@/graphql/mutations/auth/logout.gql'
import setTimezone from '@/graphql/mutations/setTimezone.gql'

import { BILLING_TERM_YEAR } from '@/lib/constants'
import { UserPrefs } from '@/lib/prefs'

let prefs = new UserPrefs()

export const state = () => ({
  hasLoadedProfile: false,
  finishedInitialLoad: false,
  postponeHasUpdate: false,
  startedTour: false,
  finishedTour: false,
  preferredEditorMode: prefs.editorMode,
  profile: null,
  savedCommentReplies: []
})

export const mutations = {
  finishedInitialLoad(state) {
    state.finishedInitialLoad = true
  },
  setPostponeUpdated(state) {
    state.postponeHasUpdate = true
  },
  startTour(state) {
    state.startedTour = true
  },
  finishTour(state) {
    state.finishedTour = true
  },
  profile(state, profile) {
    state.profile = profile
    state.hasLoadedProfile = true
  },
  preferredEditorMode(state, editorMode) {
    prefs.editorMode = editorMode
    state.preferredEditorMode = editorMode
  },
  checkOnboardingItem(state, onboardingItemName) {
    state.profile[onboardingItemName] = true
  },
  // Saved comment replies
  setSavedCommentReplies(state, savedCommentReplies) {
    state.savedCommentReplies = savedCommentReplies
  },
  addSavedCommentReply(state, savedCommentReply) {
    state.savedCommentReplies.push(savedCommentReply)
  },
  removeSavedCommentReply(state, savedCommentReplyId) {
    state.savedCommentReplies = state.savedCommentReplies.filter(
      (reply) => reply.id !== savedCommentReplyId
    )
  }
}

export const actions = {
  async logout(context) {
    const apolloClient = this.app.apolloProvider.defaultClient
    const noAuthApolloClient = this.app.apolloProvider.clients.noauth
    await noAuthApolloClient.mutate({ mutation: logout })
    await onLogout(noAuthApolloClient)
    await onLogout(apolloClient)

    context.commit('profile', null)

    Sentry.configureScope(function (scope) {
      scope.setUser(null)
    })
  },
  async getProfile(context) {
    const apolloClient = this.app.apolloProvider.defaultClient
    const noAuthApolloClient = this.app.apolloProvider.clients.noauth
    const response = await noAuthApolloClient.query({
      query: profile,
      fetchPolicy: 'network-only'
    })

    const userProfile = response.data.profile
    context.commit('profile', userProfile)

    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone

    const isImpersonating = window.location.href.includes('impersonate')

    if (userProfile) {
      if (HelpCrunch && !this.app.$config.dev && !isImpersonating) {
        HelpCrunch('updateUser', {
          user_id: userProfile.hashedId,
          email: userProfile.email,
          name:
            userProfile.account.name ||
            userProfile.fullName ||
            userProfile.username,
          custom_data: {
            username: userProfile.username,
            planCode: userProfile.account.plan.code,
            planLabel: userProfile.account.plan.label,
            planMonthlyPrice: userProfile.account.plan.monthlyPrice,
            hasPremiumSupport: userProfile.account.hasPremiumSupport,
            registeredAt: userProfile.registeredAt,
            timezone: userProfile.timezone,
            numRedditAccounts: userProfile.redditAccounts.length,
            billingTerm: userProfile.account.billingTerm,
            subscriptionStatus: userProfile.account.subscriptionStatus,
            stripeCustomerId: userProfile.account.stripeCustomerId,
            stripePlanId:
              userProfile.account.plan.stripeMonthlyId ||
              userProfile.account.plan.stripeAnnualId,
            appVersion: this.app.$config.appVersion
          }
        })
      }

      Sentry.configureScope(function (scope) {
        scope.setUser({
          id: userProfile.id,
          email: userProfile.email,
          username: userProfile.username
        })
      })
    }

    // Set the user's initial timezone
    if (userProfile && !userProfile.timezone) {
      apolloClient.mutate({
        mutation: setTimezone,
        variables: {
          timezone: tz
        }
      })
    }
  },
  async finishedInitialLoad(context) {
    context.commit('finishedInitialLoad')
  },
  async changePreferredEditorMode(context, editorMode) {
    context.commit('preferredEditorMode', editorMode)
  },
  async loadSavedCommentReplies(context) {
    let response = await this.app.apolloProvider.defaultClient.query({
      query: savedCommentReplies,
      fetchPolicy: 'network-only'
    })
    context.commit('setSavedCommentReplies', response.data.savedCommentReplies)
  }
}

export const getters = {
  plan(state) {
    if (state.profile) {
      return state.profile.account.plan
    } else {
      return null
    }
  },
  isOnYearlyBillingTerm(state) {
    if (state.profile) {
      return state.profile.account.billingTerm === BILLING_TERM_YEAR
    } else {
      return false
    }
  },
  isLoggedIn(state) {
    return !!state.profile
  },
  flagDMFeatureEnabled(state) {
    return true
  },
  monthlyPostUsagePercentage(state) {
    if (!state.profile) {
      return 0.0
    }
    return (
      state.profile.account.postsSubmittedThisMonth /
      state.profile.account.allowedMonthlyPosts
    )
  },
  monthlyDMUsagePercentage(state) {
    if (!state.profile) {
      return 0.0
    }
    return (
      state.profile.account.dmsSentThisMonth /
      state.profile.account.allowedMonthlyDms
    )
  }
}
